/**
 * LandingProductsPage — 投放产品配置（ADM-LP-S001）
 *
 * 全局模块：不按顶栏 App 切分。列表 / 新建 / 编辑 / 操作记录接真。
 * 任务、生成、发布、文案、logo 上传不在本页（S003–S007 / S001 plan NOT-in-scope）。
 */

import type { LandingProductDto } from '@novel-admin/shared';
import { useState } from 'react';
import { formatLandingAppLabels } from '~/data/adapters/landing-products-adapter';
import { useAuditLogsPage, useLandingProductsPage } from '~/data/queries';
import { humanizeAuditAction } from '~/lib/audit-action-meta';
import { fmtDateTime } from '~/lib/format';
import { Icons } from '~/lib/icons';
import { ContextMenu } from '~/ui/ContextMenu';
import { Drawer } from '~/ui/Dialog';
import { Pagination } from '~/ui/Pagination';
import { SearchInput } from '~/ui/SearchInput';
import { Section } from '~/ui/Section';
import { Table } from '~/ui/Table';
import { LandingCopyEditor } from './dialogs/LandingCopyEditor';
import { LandingProductEditor } from './dialogs/LandingProductEditor';
import { LandingProductRegenerationDialog } from './projects/LandingProductRegenerationDialog';

const PAGE_SIZE_OPTIONS = [20, 50];

export default function LandingProductsPage({ embedded = false }: { embedded?: boolean }) {
  void embedded;
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [q, setQ] = useState('');
  const [editor, setEditor] = useState<LandingProductDto | 'new' | null>(null);
  const [copyOf, setCopyOf] = useState<LandingProductDto | null>(null);
  const [regenerationProduct, setRegenerationProduct] = useState<NonNullable<
    LandingProductDto['html_change']
  > | null>(null);
  const [auditOf, setAuditOf] = useState<{ id: string; name: string } | null>(null);

  const { data, total, isLoading, isError } = useLandingProductsPage({ page, pageSize });

  const filtered = q
    ? data.filter((row) => {
        const hay =
          `${row.name} ${row.brand} ${row.app_ids.join(' ')} ${formatLandingAppLabels(row)}`.toLowerCase();
        return hay.includes(q.toLowerCase());
      })
    : data;

  return (
    <div className="vstack" style={{ gap: 12 }}>
      <Section
        title="投放产品"
        sub="产品与 App 一一对应（product 1 ── 1 app）· 承载落地页渲染身份 · 本期无删除端点"
        action={
          <button type="button" className="btn btn-sm btn-accent" onClick={() => setEditor('new')}>
            <Icons.IconPlus size={11} />
            新建产品
          </button>
        }
      >
        <div className="hstack" style={{ gap: 8, marginBottom: 10 }}>
          <SearchInput
            value={q}
            placeholder="搜索当前页产品名 / 品牌 / App"
            onChange={(e) => setQ(e.target.value)}
            onClear={() => setQ('')}
            width={280}
            size="sm"
          />
        </div>
        {isError && (
          <div style={{ padding: '10px 0', fontSize: 'var(--text-sm)', color: 'var(--neg)' }}>
            产品列表加载失败
          </div>
        )}
        <div style={{ overflowX: 'auto' }}>
          <Table className="tbl">
            <thead>
              <tr>
                <th>产品</th>
                <th>关联 App</th>
                <th>OneLink template</th>
                <th>投放页域 base_url</th>
                <th>埋点</th>
                <th>logo</th>
                <th>页面文案</th>
                <th>更新</th>
                <th style={{ width: 36 }} />
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td
                    colSpan={9}
                    style={{ padding: 24, color: 'var(--fg-muted)', fontSize: 'var(--text-base)' }}
                  >
                    加载中…
                  </td>
                </tr>
              )}
              {!isLoading &&
                filtered.map((row) => {
                  const identityOk = [
                    'bg',
                    'primary',
                    'primary_deep',
                    'primary_dark',
                    'primary_soft',
                  ].every((k) => Boolean(row.theme[k as keyof typeof row.theme]));
                  return (
                    <tr key={row.id} style={{ cursor: 'pointer' }} onClick={() => setEditor(row)}>
                      <td>
                        <div className="hstack" style={{ gap: 8 }}>
                          <div
                            style={{
                              width: 26,
                              height: 26,
                              borderRadius: 'var(--radius)',
                              background: row.theme.primary,
                              flexShrink: 0,
                            }}
                          />
                          <div>
                            <button
                              type="button"
                              className="btn btn-ghost"
                              style={{ padding: 0, fontWeight: 500, fontSize: 'var(--text-sm)' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditor(row);
                              }}
                            >
                              {row.name}
                            </button>
                            <div
                              className="mono"
                              style={{ fontSize: 'var(--text-2xs)', color: 'var(--fg-faint)' }}
                            >
                              brand · {row.brand}
                            </div>
                          </div>
                          {!identityOk && (
                            <span
                              className="chip chip-warn"
                              style={{ fontSize: 'var(--text-2xs)' }}
                              title="身份色 5 键必填"
                            >
                              主题缺键
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="hstack" style={{ gap: 4, flexWrap: 'wrap' }}>
                          {formatLandingAppLabels(row)
                            .split('、')
                            .filter(Boolean)
                            .map((label) => (
                              <span
                                key={label}
                                className="chip"
                                style={{ fontSize: 'var(--text-2xs)' }}
                              >
                                {label}
                              </span>
                            ))}
                          {row.app_ids.length !== 1 && (
                            <span className="chip chip-warn">需选择唯一 App · 编辑产品可纠正</span>
                          )}
                        </div>
                      </td>
                      <td className="mono" style={{ fontSize: 'var(--text-2xs)' }}>
                        {row.onelink_base_url?.replace(/^https:\/\//, '') ?? 'Web-only · 不适用'}
                      </td>
                      <td className="mono" style={{ fontSize: 'var(--text-2xs)' }}>
                        {row.base_url.replace(/^https:\/\//, '')}
                        {row.base_url.includes('lp-dev') && (
                          <span
                            className="chip chip-warn"
                            style={{ fontSize: 'var(--text-2xs)', marginLeft: 4 }}
                          >
                            dev / staging
                          </span>
                        )}
                      </td>
                      <td>
                        {row.tracking_enabled ? (
                          <span className="chip chip-pos" style={{ fontSize: 'var(--text-2xs)' }}>
                            已注入 /collect
                          </span>
                        ) : (
                          <span className="chip chip-warn" style={{ fontSize: 'var(--text-2xs)' }}>
                            整体关闭
                          </span>
                        )}
                      </td>
                      <td>
                        {row.logo ? (
                          <span
                            className="mono"
                            style={{ fontSize: 'var(--text-2xs)', color: 'var(--fg-muted)' }}
                          >
                            {row.logo.r2_key?.split('/').pop() ?? '已配'}
                          </span>
                        ) : (
                          <span
                            className="chip chip-warn"
                            style={{ fontSize: 'var(--text-2xs)' }}
                            title="未配 logo：该产品的任务不能开启顶部下载条"
                          >
                            未配
                          </span>
                        )}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="chip mono"
                          style={{ fontSize: 'var(--text-2xs)' }}
                          title="页面文案配置（主键 product_id + language）"
                          onClick={(e) => {
                            e.stopPropagation();
                            setCopyOf(row);
                          }}
                        >
                          按语言配置
                        </button>
                      </td>
                      <td
                        className="mono"
                        style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-muted)' }}
                      >
                        {fmtDateTime(row.updated_at)}
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <ContextMenu
                          items={[
                            { label: '编辑产品', icon: 'IconEdit', onClick: () => setEditor(row) },
                            {
                              label: '页面文案配置',
                              icon: 'IconType',
                              onClick: () => setCopyOf(row),
                            },
                            '---',
                            {
                              label: '操作记录',
                              icon: 'IconHistory',
                              onClick: () => setAuditOf({ id: row.id, name: row.name }),
                            },
                            {
                              label: '删除产品',
                              icon: 'IconTrash',
                              disabled: true,
                              title: '本期无删除端点',
                            },
                          ]}
                        >
                          <button
                            type="button"
                            className="btn btn-ghost btn-icon"
                            style={{ width: 24, height: 24 }}
                            aria-label="产品操作"
                          >
                            <Icons.IconMoreV size={12} />
                          </button>
                        </ContextMenu>
                      </td>
                    </tr>
                  );
                })}
              {!isLoading && filtered.length === 0 && (
                <tr>
                  <td
                    colSpan={9}
                    style={{ padding: 24, color: 'var(--fg-muted)', fontSize: 'var(--text-base)' }}
                  >
                    {total === 0 ? '还没有投放产品' : '当前页没有匹配的产品'}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </Section>

      <div
        className="card"
        style={{ padding: '12px 14px', background: 'var(--info-soft)', borderColor: 'transparent' }}
      >
        <div className="hstack" style={{ gap: 10, alignItems: 'flex-start' }}>
          <Icons.IconInfo size={13} style={{ color: 'var(--info)', marginTop: 2 }} />
          <div style={{ fontSize: 'var(--text-xs)', lineHeight: 1.6 }}>
            产品字段变更<span style={{ fontWeight: 500 }}>不</span>自动重生成既有任务。归因 / 埋点 /
            OneLink 相关字段改动后，受影响任务须
            <span style={{ fontWeight: 500 }}>重新生成、重新下载并重新发布</span>
            才生效——已发布页面的参数在生成时固化进 HTML。
          </div>
        </div>
      </div>

      <Pagination
        total={total}
        page={page}
        pageSize={pageSize}
        sizeOptions={PAGE_SIZE_OPTIONS}
        onChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
      />

      {editor !== null && (
        <LandingProductEditor
          product={editor === 'new' ? null : editor}
          onClose={() => setEditor(null)}
          onRenderingChanged={setRegenerationProduct}
          onOpenAudit={
            editor === 'new'
              ? undefined
              : () => {
                  setAuditOf({ id: editor.id, name: editor.name });
                }
          }
        />
      )}
      {copyOf && <LandingCopyEditor product={copyOf} onClose={() => setCopyOf(null)} />}

      {regenerationProduct && (
        <LandingProductRegenerationDialog
          change={regenerationProduct}
          onClose={() => setRegenerationProduct(null)}
        />
      )}
      {auditOf && <LandingProductAuditDrawer product={auditOf} onClose={() => setAuditOf(null)} />}
    </div>
  );
}

function LandingProductAuditDrawer({
  product,
  onClose,
}: {
  product: { id: string; name: string };
  onClose: () => void;
}) {
  const {
    data: logs,
    total,
    isLoading,
    isError,
    isServiceUpdating,
  } = useAuditLogsPage({
    page: 1,
    limit: 50,
    filters: { resource: 'landing_products', resource_id: product.id },
  });

  return (
    <Drawer
      title={`操作记录 · ${product.name}`}
      subtitle="该投放产品的配置变更（操作人 / 时间 / 动作）"
      icon="IconHistory"
      onClose={onClose}
    >
      {isLoading ? (
        <div
          style={{
            padding: 24,
            textAlign: 'center',
            color: 'var(--fg-muted)',
            fontSize: 'var(--text-sm)',
          }}
        >
          加载中…
        </div>
      ) : isError ? (
        <div
          style={{
            padding: 24,
            textAlign: 'center',
            color: 'var(--neg)',
            fontSize: 'var(--text-sm)',
          }}
        >
          {isServiceUpdating ? '服务更新中' : '操作记录加载失败'}
        </div>
      ) : logs.length === 0 ? (
        <div
          style={{
            padding: 24,
            textAlign: 'center',
            color: 'var(--fg-muted)',
            fontSize: 'var(--text-sm)',
          }}
        >
          暂无变更记录
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <Table className="tbl">
            <thead>
              <tr>
                <th>时间</th>
                <th>动作</th>
                <th>操作人</th>
                <th className="num">状态</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((l) => (
                <tr key={l.id}>
                  <td className="mono">{fmtDateTime(l.created_at)}</td>
                  <td>{humanizeAuditAction(l.action).label}</td>
                  <td className="mono">{l.actor_id}</td>
                  <td className="num mono">{l.status}</td>
                </tr>
              ))}
            </tbody>
          </Table>
          {total > logs.length && (
            <div style={{ padding: 8, fontSize: 'var(--text-xs)', color: 'var(--fg-faint)' }}>
              显示最近 {logs.length} / 共 {total} 条
            </div>
          )}
        </div>
      )}
    </Drawer>
  );
}
